/* eslint-disable jsx-a11y/media-has-caption */
import CropRotate from '@mui/icons-material/CropRotate';
import {
  Box,
  Button,
  ButtonBase,
  IconButton,
  SvgIcon,
  ToggleButton,
  ToggleButtonGroup,
  Toolbar,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import ChatPanelContext from 'components/@home/@messages/ChatPanel/ChatPanelContext';
import FileThumbnail from 'components/@home/@messages/ChatPanel/MessagesList/MessageGroup/FileThumbnail';
import Loading from 'components/common/Loading';
import ResizableDraggable from 'components/common/ResizableDraggable';
import isEmpty from 'lodash/isEmpty';
import mapValues from 'lodash/mapValues';
import CloseIcon from 'mdi-react/CloseIcon';
import EditIcon from 'mdi-react/EditIcon';
import FileIcon from 'mdi-react/FileIcon';
import MicrophoneIcon from 'mdi-react/MicrophoneIcon';
import PlayIcon from 'mdi-react/PlayIcon';
import UndoIcon from 'mdi-react/UndoIcon';
import PropTypes from 'prop-types';
import { useCompanyFeature } from 'utils/useCompanyFeature';
import { Divider } from '@mui/material';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import CanvasDraw from 'react-canvas-draw';
import drawImage from 'react-canvas-draw/lib/drawImage';
import useDimensions from 'react-cool-dimensions';
import useChatComposerLinkPopover from 'src/hooks/useChatComposerLinkPopover';
import applyContentEditableFormat from 'utils/applyContentEditableFormat';
import { I18n } from 'utils/i18n';
import filesize from 'utils/filesize';
import ChatInput from 'components/@home/@messages/ChatPanel/SendForm/controls/ChatField/ChatInput';
import FormatToolbar from 'components/@home/@messages/ChatPanel/SendForm/buttons/FormatToolbar';
import LinkPopover from 'components/@home/@messages/ChatPanel/SendForm/buttons/LinkPopover';
import InitiativeTagSelector from 'components/@home/@messages/ChatPanel/SendForm/InitiativeTagSelector';
import canTagInitiative from 'utils/canTagInitiative';
import { revokePreviewObjectUrls } from 'utils/compressImages';
import currentChannel from 'store/selectors/currentChannel';
import isCompanyAdmin from 'store/selectors/isCompanyAdmin';

const Root = styled('div')({
  flex: '1 0 auto',
  display: 'flex',
  flexDirection: 'column',
});

const ActionsRow = styled('div')(({ theme }) => ({
  display: 'flex',
  flex: '1 0 auto',
  flexDirection: 'row',
  gap: theme.spacing(1),
  alignItems: 'center',
  justifyContent: 'center',
}));

const ImagesArea = styled('div')({
  flex: '1 0 auto',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexDirection: 'column',
  position: 'relative',
});

const ImageFrame = styled('div')({
  position: 'absolute',
  display: 'flex',
  justifyContent: 'center',
});

const thumbSx = theme => ({
  width: 64,
  height: 64,
  objectFit: 'cover',
  borderWidth: 1,
  borderStyle: 'solid',
  borderColor: theme.palette.primary.extraLight,
  borderRadius: theme.shape.borderRadius,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

const fileIconSx = theme => ({
  width: 64,
  height: 64,
  borderRadius: theme.shape.borderRadius,
  color: theme.palette.primary.light,
  borderWidth: 1,
  borderStyle: 'solid',
  borderColor: theme.palette.primary.extraLight,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

const colors = [
  '#434343',
  '#9DA0A9',
  '#FFFFFF',
  '#33CEFF',
  '#64DC2F',
  '#FFEA3B',
  '#F49226',
  '#FF4A4A',
];

const DEFAULT_COLOR = colors[3];

const radii = [3, 5, 7, 11];

const DEFAULT_RADIUS = radii[0];

const ImageUploadPreview = ({ onSend, isUploading = false, progress = null }) => {
  const [dimensions, setDimensions] = useState({ width: 0, height: 0, aspectRatio: 0 });
  const { observe } = useDimensions({
    onResize: ({ width, height }) => {
      setDimensions({ width, height, aspectRatio: width / height });
    },
  });
  const {
    imagesToUpload,
    setImagesToUpload,
    setProps,
    attachmentInitiativeTag,
    setAttachmentInitiativeTag,
  } = useContext(ChatPanelContext);
  const channel = useSelector(currentChannel);
  const isCompanyAdminUser = useSelector(isCompanyAdmin);
  const areInitiativesEnabled = useCompanyFeature('initiatives');
  const canAttachInitiative = canTagInitiative(channel, isCompanyAdminUser);
  const [currentImageIdx, setCurrentImageIdx] = useState(0);
  const canvasRefs = useRef([]);
  const addRef = idx => el => {
    canvasRefs.current[idx] = el;
  };
  const [state, setState] = useState('none');
  const drawing = state === 'drawing';
  const cropping = state === 'cropping';
  const [radius, setRadius] = useState(DEFAULT_RADIUS);
  const [color, setColor] = useState(DEFAULT_COLOR);
  const currentImage = imagesToUpload[currentImageIdx];
  const changeState = useCallback(
    (e, newState) => {
      setState(oldState => (newState === oldState ? 'none' : newState));
    },
    [setState],
  );
  const exportImage = useCallback(() => {
    if (!currentImage?.src || !dimensions.aspectRatio) {
      return;
    }
    setTimeout(() => {
      const canvasDraw = canvasRefs.current[currentImageIdx];
      if (!canvasDraw?.canvas?.drawing) {
        return;
      }
      const img = document.createElement('img');
      img.crossOrigin = 'anonymous';
      img.src = currentImage.origSrc;
      img.onload = () => {
        const canvasRef = canvasRefs.current[currentImageIdx];
        if (!canvasRef?.canvas?.drawing) {
          return;
        }
        const srcCanvas = canvasRef.canvas.drawing;
        const destCanvas = document.createElement('canvas');
        const croppingBox = mapValues(currentImage.croppingBox, (v, k, o) => v * o.scale);
        destCanvas.width = croppingBox.width;
        destCanvas.height = croppingBox.height;
        const destCtx = destCanvas.getContext('2d');
        destCtx.globalCompositeOperation = 'source-over';
        drawImage({
          ctx: destCtx,
          img,
          x: -croppingBox.left,
          y: -croppingBox.top,
          w: currentImage.width,
          h: currentImage.height,
        });
        const croppedDataUrl = destCanvas.toDataURL('image/png');
        destCtx.drawImage(srcCanvas, 0, 0, destCanvas.width, destCanvas.height);
        const dataURL = destCanvas.toDataURL('image/png');
        setProps({ src: dataURL, croppedSrc: croppedDataUrl }, currentImageIdx);
      };
    }, 0);
  }, [
    currentImage?.croppingBox,
    currentImage?.height,
    currentImage?.origSrc,
    currentImage?.src,
    currentImage?.width,
    currentImageIdx,
    dimensions.aspectRatio,
    setProps,
  ]);
  const handleResize = box => {
    const canvasDraw = canvasRefs.current[currentImageIdx];
    if (canvasDraw) {
      canvasDraw.clear();
    }
    setProps({ croppingBox: box }, currentImageIdx);
  };

  useEffect(() => {
    if (currentImage) {
      exportImage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- export only when cropping box changes, not on every image object update
  }, [currentImage?.croppingBox]);

  const handleDrawEnd = useCallback(() => {
    exportImage();
  }, [exportImage]);
  const undo = () => {
    canvasRefs.current?.[currentImageIdx]?.undo();
    handleDrawEnd();
  };
  useEffect(() => {
    if (!canvasRefs.current[currentImageIdx]) {
      return () => {};
    }
    const canvas = canvasRefs.current[currentImageIdx].canvas.interface;
    if (state === 'drawing') {
      canvas.addEventListener('mouseup', handleDrawEnd);
      canvas.addEventListener('mouseout', handleDrawEnd);
      canvas.addEventListener('touchend', handleDrawEnd);
      canvas.addEventListener('touchcancel', handleDrawEnd);
    }
    return () => {
      canvas.removeEventListener('mouseup', handleDrawEnd);
      canvas.removeEventListener('mouseout', handleDrawEnd);
      canvas.removeEventListener('touchend', handleDrawEnd);
      canvas.removeEventListener('touchcancel', handleDrawEnd);
    };
  }, [state, currentImageIdx, dimensions.aspectRatio, handleDrawEnd]);
  const close = useCallback(() => {
    revokePreviewObjectUrls(imagesToUpload);
    setImagesToUpload([]);
  }, [imagesToUpload, setImagesToUpload]);
  const captionInputRef = useRef(null);
  const suggestionsPortalRef = useRef(null);
  const {
    linkPopover,
    closeLinkPopover,
    handleLinkToolbarClick,
    handleLinkEditFromInput,
    handleLinkApply,
    handleLinkRemove,
    handleLinkShortcut,
  } = useChatComposerLinkPopover(captionInputRef);
  const [formatState, setFormatState] = useState({
    bold: false,
    italic: false,
    strikethrough: false,
    code: false,
    link: false,
  });
  const handleChange = useCallback(
    e => {
      setProps({ caption: e.target.value }, currentImageIdx);
    },
    [currentImageIdx, setProps],
  );
  const handleWrapSelection = useCallback((prefix, suffix) => {
    if (!captionInputRef.current) return;
    applyContentEditableFormat(captionInputRef.current, prefix, suffix);
  }, []);
  const captionValue = useMemo(() => {
    const c = currentImage?.caption;
    if (!c) return { markup: '', text: '', mentions: [] };
    if (typeof c === 'string') return { markup: c, text: c, mentions: [] };
    return c;
  }, [currentImage?.caption]);
  const select = idx => () => setCurrentImageIdx(idx);
  return (
    <Root>
      <Toolbar>
        <IconButton
          sx={theme => ({ color: theme.palette.primary.ultraDark })}
          edge="end"
          aria-label="close"
          onClick={close}
          size="large"
        >
          <CloseIcon />
        </IconButton>
        {currentImage.src && currentImage.file?.type !== 'image/gif' ? (
          <ActionsRow>
            <ToggleButtonGroup
              sx={theme => ({
                gap: theme.spacing(1),
                '& .MuiToggleButtonGroup-grouped': {
                  border: 'none',
                  borderRadius: '50%',
                },
              })}
              onChange={changeState}
              value={state}
              exclusive
            >
              <ToggleButton sx={{ borderRadius: '50%', border: 0 }} value="drawing">
                <EditIcon />
              </ToggleButton>
              <ToggleButton sx={{ borderRadius: '50%', border: 0 }} value="cropping">
                <CropRotate />
              </ToggleButton>
            </ToggleButtonGroup>
            <IconButton
              onClick={undo}
              disabled={
                isEmpty(canvasRefs.current?.[currentImageIdx]?.lines) &&
                isEmpty(canvasRefs.current?.[currentImageIdx]?.erasedLines)
              }
              size="large"
            >
              <UndoIcon />
            </IconButton>
          </ActionsRow>
        ) : null}
        <Button
          sx={theme => ({ color: theme.palette.primary.dark, fontWeight: 'normal' })}
          style={{ visibility: cropping || drawing ? 'visible' : 'hidden' }}
          type="button"
          onClick={() => setState('none')}
        >
          {I18n.t('SendMessageForm.Done')}
        </Button>
      </Toolbar>
      <ImagesArea ref={observe}>
        {!!dimensions.aspectRatio &&
          imagesToUpload.map(img => {
            if (img.src) {
              const aspectRatio = cropping ? img.aspectRatio : img.croppingBox.aspectRatio;
              const src = cropping ? img.origSrc : img.croppedSrc;
              const width =
                aspectRatio > dimensions.aspectRatio
                  ? dimensions.width * 0.8
                  : dimensions.height * 0.8 * aspectRatio;
              const height =
                aspectRatio > dimensions.aspectRatio
                  ? (dimensions.width * 0.8) / aspectRatio
                  : dimensions.height * 0.8;
              if (img.file?.type === 'image/gif') {
                return (
                  <ImageFrame
                    key={img.idx}
                    style={{ visibility: currentImageIdx !== img.idx ? 'hidden' : undefined }}
                  >
                    <img src={img.origSrc} alt="" style={{ width, height, objectFit: 'contain' }} />
                  </ImageFrame>
                );
              }
              return (
                <ImageFrame
                  key={img.idx}
                  style={{ visibility: currentImageIdx !== img.idx ? 'hidden' : undefined }}
                >
                  <CanvasDraw
                    style={{ visibility: cropping ? 'hidden' : undefined }}
                    hideGrid
                    hideInterface={!drawing}
                    lazyRadius={10}
                    canvasWidth={width}
                    canvasHeight={height}
                    catenaryColor="transparent"
                    disabled={!drawing || currentImageIdx !== img.idx}
                    brushRadius={radius}
                    brushColor={color}
                    imgSrc={src}
                    ref={addRef(img.idx)}
                  />
                  {cropping && (
                    <>
                      <img
                        src={img.origSrc}
                        alt=""
                        style={{
                          position: 'absolute',
                          display: 'flex',
                          justifyContent: 'center',
                          width,
                          height,
                        }}
                      />
                      <ResizableDraggable
                        imgWidth={img.width}
                        box={img.croppingBox}
                        onResize={handleResize}
                      />
                    </>
                  )}
                </ImageFrame>
              );
            }
            if (img.file.type.startsWith('video/')) {
              return (
                <video
                  key={img.idx}
                  style={{
                    maxWidth: '90%',
                    maxHeight: '90%',
                    position: 'absolute',
                    display: 'flex',
                    justifyContent: 'center',
                    visibility: currentImageIdx !== img.idx ? 'hidden' : undefined,
                  }}
                  controls
                >
                  <source src={URL.createObjectURL(img.file)} />
                </video>
              );
            }
            if (img.file.type.startsWith('audio/')) {
              return (
                <Box
                  key={img.idx}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    flexDirection: 'column',
                    visibility: currentImageIdx !== img.idx ? 'hidden' : undefined,
                  }}
                >
                  <Box sx={theme => ({ display: 'flex', color: theme.palette.primary.main })}>
                    <SvgIcon>
                      <MicrophoneIcon />
                    </SvgIcon>
                    <Box
                      sx={{
                        flexGrow: 1,
                        paddingLeft: '10px',
                        paddingRight: '20px',
                        color: 'inherit',
                        cursor: 'pointer',
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: '16px',
                          fontWeight: '500',
                          lineHeight: '1.38',
                          letterSpacing: '-0.4px',
                          color: 'inherit',
                        }}
                      >
                        {img.file.name}
                      </Typography>
                      <Typography sx={{ fontSize: 12, color: 'inherit' }}>
                        {filesize(img.file.size || 0)}
                      </Typography>
                    </Box>
                  </Box>
                  <audio controls>
                    <source src={URL.createObjectURL(img.file)} />
                  </audio>
                </Box>
              );
            }
            return (
              <ImageFrame
                key={img.idx}
                style={{ visibility: currentImageIdx !== img.idx ? 'hidden' : undefined }}
              >
                <FileThumbnail key={img.idx} data={currentImage.file} />
              </ImageFrame>
            );
          })}
      </ImagesArea>
      <Box sx={{ flex: 0, display: 'flex', flexDirection: 'column' }}>
        <Box
          sx={theme => ({
            marginBottom: theme.spacing(3),
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          })}
        >
          {drawing ? (
            <Box sx={{ display: 'flex', flex: '1 0 auto', gap: '25%', justifyContent: 'center' }}>
              <Box sx={theme => ({ display: 'flex', gap: theme.spacing(1) })}>
                {colors.map(backgroundColor => (
                  <IconButton
                    key={backgroundColor}
                    style={{
                      backgroundColor,
                      transform: `scale(${backgroundColor === color ? 1.2 : 0.9})`,
                    }}
                    onClick={() => setColor(backgroundColor)}
                    size="large"
                  />
                ))}
              </Box>
              <Box sx={theme => ({ display: 'flex', gap: theme.spacing(1) })}>
                {radii.map(rad => (
                  <ButtonBase
                    sx={theme => ({ width: theme.spacing(3), height: theme.spacing(3) })}
                    key={rad}
                    onClick={() => setRadius(rad)}
                  >
                    <Box
                      sx={{
                        borderRadius: '50%',
                        backgroundColor: radius === rad ? '#fff' : '#ccc',
                        width: rad * window.devicePixelRatio,
                        height: rad * window.devicePixelRatio,
                      }}
                    />
                  </ButtonBase>
                ))}
              </Box>
            </Box>
          ) : (
            <Box
              sx={theme => ({
                width: '70%',
                display: 'flex',
                flexDirection: 'column',
                backgroundColor: theme.palette.background.paper,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: '12px',
                overflow: 'hidden',
              })}
            >
              <div ref={suggestionsPortalRef} />
              <Box sx={theme => ({ padding: theme.spacing(1, 1.5) })}>
                <ChatInput
                  name="caption"
                  value={captionValue}
                  onChange={handleChange}
                  onFormatStateChange={setFormatState}
                  onLinkShortcut={handleLinkShortcut}
                  onLinkEdit={handleLinkEditFromInput}
                  inputRef={captionInputRef}
                  placeholder={I18n.t('ImageUploadPreview.Caption')}
                  onPaste={() => {}}
                  onKeyPress={() => {}}
                  suggestionsPortal={suggestionsPortalRef}
                />
              </Box>
              <Box
                sx={theme => ({
                  display: 'flex',
                  alignItems: 'center',
                  borderTop: `1px solid ${theme.palette.divider}`,
                  padding: theme.spacing(0.5, 1),
                  ...(theme.palette.mode === 'light' && {
                    backgroundColor: theme.palette.secondary.ultraUltraLight,
                  }),
                })}
              >
                <FormatToolbar
                  onWrapSelection={handleWrapSelection}
                  onLinkClick={handleLinkToolbarClick}
                  activeFormats={formatState}
                />
                {areInitiativesEnabled && canAttachInitiative ? (
                  <>
                    <Divider orientation="vertical" sx={{ mx: 1, height: '1rem' }} />
                    <InitiativeTagSelector
                      value={attachmentInitiativeTag}
                      onChange={setAttachmentInitiativeTag}
                    />
                  </>
                ) : null}
              </Box>
            </Box>
          )}
        </Box>
        <Toolbar
          sx={theme => ({
            display: 'flex',
            justifyContent: 'center',
            background: theme.palette.white,
          })}
        >
          <Box sx={{ flex: '1 0 auto', display: 'flex', justifyContent: 'center', gap: '10px' }}>
            {imagesToUpload.map((img, idx) => (
              <Button key={img.idx} onClick={select(idx)}>
                {(() => {
                  if (img.src) {
                    return (
                      <Box
                        component="img"
                        alt=""
                        src={img.src}
                        sx={theme => ({
                          ...thumbSx(theme),
                          ...(idx === currentImageIdx && {
                            borderColor: theme.palette.primary.main,
                            borderWidth: 3,
                          }),
                        })}
                      />
                    );
                  }
                  if (img.file.type.startsWith('video/')) {
                    return (
                      <Box
                        sx={theme => ({
                          ...thumbSx(theme),
                          position: 'relative',
                          ...(idx === currentImageIdx && {
                            borderColor: theme.palette.primary.main,
                            borderWidth: 3,
                          }),
                        })}
                      >
                        <Box
                          sx={theme => ({
                            position: 'absolute',
                            padding: '2px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: 'rgba(0,0,0,0.3)',
                            borderRadius: '50%',
                            color: theme.palette.white,
                          })}
                        >
                          <PlayIcon />
                        </Box>
                        <video style={{ width: 58, height: 58 }}>
                          <source src={URL.createObjectURL(img.file)} />
                        </video>
                      </Box>
                    );
                  }
                  if (img.file.type.startsWith('audio/')) {
                    return (
                      <Box
                        sx={theme => ({
                          ...fileIconSx(theme),
                          ...(idx === currentImageIdx && {
                            borderColor: theme.palette.primary.main,
                            borderWidth: 3,
                          }),
                        })}
                      >
                        <MicrophoneIcon />
                      </Box>
                    );
                  }
                  return (
                    <Box
                      sx={theme => ({
                        ...fileIconSx(theme),
                        ...(idx === currentImageIdx && {
                          borderColor: theme.palette.primary.main,
                          borderWidth: 3,
                        }),
                      })}
                    >
                      <FileIcon />
                    </Box>
                  );
                })()}
              </Button>
            ))}
          </Box>
          {isUploading ? (
            <Box sx={{ padding: '6px 8px' }}>
              <Loading value={progress} debounce={0} size={30} />
            </Box>
          ) : (
            <Button
              sx={theme => ({ color: theme.palette.primary.dark, fontWeight: 'bold' })}
              type="button"
              onClick={onSend}
            >
              {I18n.t('SendMessageForm.Send')}
            </Button>
          )}
        </Toolbar>
      </Box>
      <LinkPopover
        open={linkPopover.open}
        anchorEl={linkPopover.anchorEl}
        onClose={closeLinkPopover}
        onApply={handleLinkApply}
        onRemove={linkPopover.showRemove ? handleLinkRemove : undefined}
        initialUrl={linkPopover.initialUrl}
        initialText={linkPopover.initialText}
        showRemove={linkPopover.showRemove}
      />
    </Root>
  );
};

ImageUploadPreview.propTypes = {
  onSend: PropTypes.func.isRequired,
  isUploading: PropTypes.bool,
  progress: PropTypes.number,
};

export default ImageUploadPreview;
